import { toast } from "sonner";

export type NoticePopupTone = "error" | "warning";

export interface NoticePopup {
  id: number;
  tone: NoticePopupTone;
  title: string;
  message: string;
}

const DEFAULT_TITLES: Record<NoticePopupTone, string> = {
  error: "We couldn't do that",
  warning: "Please note",
};

let popups: readonly NoticePopup[] = [];
let nextPopupId = 1;
const listeners = new Set<() => void>();

function publish(next: readonly NoticePopup[]) {
  popups = next;
  listeners.forEach((listener) => listener());
}

function showPopup(tone: NoticePopupTone, message: string, title = DEFAULT_TITLES[tone]) {
  // The same failure raised by several requests at once (a session that just
  // ended) shows once, not once per request.
  if (popups.some((popup) => popup.message === message)) return;
  publish([...popups, { id: nextPopupId++, tone, title, message }]);
}

/** The waiting popups, for NoticePopupHost to render through useSyncExternalStore. */
export const noticePopups = {
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot: () => popups,
  dismiss: (id: number) => publish(popups.filter((popup) => popup.id !== id)),
};

/**
 * The single entry point for user-facing notifications. Nothing outside
 * this file should import `sonner` directly - centralising it here is what
 * lets every feature share one consistent look, timing, and (if the
 * underlying library ever changes again) a one-file migration.
 *
 * Errors and warnings are popups someone has to acknowledge, because they mean
 * something didn't happen and a corner toast is easy to miss. Success and info
 * stay as toasts that clear themselves, so a routine save costs no extra click.
 */
export const notify = {
  success: (message: string, durationMs = 4000) => toast.success(message, { duration: durationMs }),
  error: (message: string) => showPopup("error", message),
  warning: (message: string) => showPopup("warning", message),
  info: (message: string, durationMs = 4000) => toast.info(message, { duration: durationMs }),
  /** A popup with its own title, for when the default one says too little. */
  alert: ({ title, message, tone = "warning" }: { title: string; message: string; tone?: NoticePopupTone }) =>
    showPopup(tone, message, title),
};
